/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo, useEffect, useRef } from 'react';
import { FieldValues, FormProvider, UseFormReset, useForm } from 'react-hook-form';
import omitBy from 'lodash.omitby';
import pickBy from 'lodash.pickby';
import { useRouter } from 'next/router';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import {
  useModal,
  formatExplorerOverallFilterData,
  applyMoreFilterAttrs,
  defaultItalyCoordinates,
} from '@restworld/utils-common';
import { Button as ButtonV2, Heading, Modal, ModalContent } from '@restworld/ui-ds-base';
import { JobPositionListParamsType } from '@restworld/data-services';

import { FilterContextValuesType, defaultJobListPayload } from '../job-explorer.types';
import { ExplorerFormContent } from './job-explorere-more-filter.components';
import { isRestaurantsExplorerAtom } from '../job-explorer-map';
import { getMapRefAtom, jobCoordinateParamsAtom } from '../job-explorer-map.atoms';
import { jobListParamsAtom } from '../job-explorer-list.atoms';
import {
  filterContextValuesAtom,
  isResetFilterAtom,
  jobExplorerPageContentAtom,
  moreFiltersAtom,
} from '../job-explorer-filters.atoms';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { ApplyMoreFiltersIcon } from '@restworld/utils-common';

export const JobExplorerMoreFilter = () => {
  const { t } = useTranslation('common');
  const [isRestaurantsExplorer] = useAtom(isRestaurantsExplorerAtom);
  const { open, close, show } = useModal();
  const moreFilters = useAtomValue(moreFiltersAtom);
  const submitFormRef = useRef<() => void>(() => null);
  const eraseFiltersRef = useRef<() => void>(() => null);

  const filterCount = useMemo(() => {
    try {
      return moreFilters
        ? Object.keys(
            omitBy(moreFilters, (value, key) => {
              return (
                value === undefined ||
                (key === 'occupation_ids' &&
                  value &&
                  Object.values(value).flat().filter(Boolean).length === 0 &&
                  key === 'occupation_ids') ||
                value?.length === 0 ||
                key === 'limit' ||
                key === 'offset' ||
                key === 'order_by' ||
                key === 'radius' ||
                key === 'lat' ||
                key === 'lon' ||
                key === 'zoom'
              );
            })
          ).length
        : 0;
    } catch (error) {
      console.error('error on counting filter', error);
      return 0;
    }
  }, [moreFilters]);
  return (
    <>
      <Modal open={show} onOpenChange={(v) => !v && close()}>
        <ModalContent
          className="md:max-w-3xl md:overflow-y-hidden md:pb-0 lg:max-w-[900px] lg:w-[900px]"
          title={
            <span
              className="flex flex-row items-center gap-1.5 justify-center min-w-[14rem]"
              data-cy="job-explorer-more-filter-title"
            >
              <ApplyMoreFiltersIcon />
              <Heading weight="semibold">{t('filter')}</Heading>
            </span>
          }
          footer={
            <div className="flex flex-row gap-5 items-center justify-center w-full pt-[14px] border-t border-neutral-40 md:border-none">
              <ButtonV2 type="button" testId="explorer-filters-submit-button" onClick={() => submitFormRef.current()}>
                {t('apply_filters')}
              </ButtonV2>
              <ButtonV2 variant="tertiary" disabled={!filterCount} onClick={() => eraseFiltersRef.current()}>
                {t('erase_filters')}
              </ButtonV2>
            </div>
          }
        >
          <OverallFilters close={close} submitFormRef={submitFormRef} eraseFiltersRef={eraseFiltersRef} />
        </ModalContent>
      </Modal>
      <div className="w-full md:w-fit  pl-0 lg:pr-1 relative border-primary-800">
        <ButtonV2
          onClick={open}
          variant="tertiary"
          StartIcon={AdjustmentsHorizontalIcon}
          disabled={isRestaurantsExplorer}
          testId={`job-explorer-more-filter-button`}
          fullwidth
          indicator={!!filterCount && !isRestaurantsExplorer ? filterCount : false}
        >
          {t('filter')}
        </ButtonV2>
      </div>
    </>
  );
};

export default JobExplorerMoreFilter;
const OverallFilters = ({
  close,
  submitFormRef,
  eraseFiltersRef,
}: {
  close: () => void;
  submitFormRef: React.MutableRefObject<() => void>;
  eraseFiltersRef: React.MutableRefObject<() => void>;
}) => {
  const { t } = useTranslation('common');

  const methods = useForm<FilterContextValuesType>({ defaultValues: { page_content: 'jps-and-venues' } });
  const filterContextValues = useAtomValue(filterContextValuesAtom);

  const { handleFilterChange } = useJobExplorerMoreFilter({ closeModal: close, reset: methods.reset });

  useEffect(() => {
    submitFormRef.current = methods.handleSubmit((values) => {
      handleFilterChange(values);
    });

    eraseFiltersRef.current = () => {
      handleFilterChange();
    };
  }, [handleFilterChange, methods.handleSubmit, submitFormRef, eraseFiltersRef, methods]);

  useEffect(() => {
    methods.reset(filterContextValues || {});
  }, [filterContextValues, methods]);

  return (
    <FormProvider {...methods}>
      <ExplorerFormContent handleFilterChange={handleFilterChange} />
    </FormProvider>
  );
};
export const useJobExplorerMoreFilter = ({
  closeModal,
  reset,
}: {
  closeModal: () => void;
  reset: UseFormReset<FieldValues>;
}) => {
  const router = useRouter();
  const [moreFilters, setMoreFilters] = useAtom(moreFiltersAtom);
  const [isResetFilter, setIsResetFilter] = useAtom(isResetFilterAtom);
  const setFilterContextValues = useSetAtom(filterContextValuesAtom);
  const [, setJobCoordinateParams] = useAtom(jobCoordinateParamsAtom);
  const jobListParams = useAtomValue(jobListParamsAtom);
  const setJobListParams = useSetAtom(jobListParamsAtom);
  const mapRef = useAtomValue(getMapRefAtom);
  const setJobExplorerPageContent = useSetAtom(jobExplorerPageContentAtom);
  // const { trackEvent } = useAnalytics();
  const filterCount = useMemo(() => (moreFilters ? Object.keys(moreFilters).length : 0), [moreFilters]);
  const shouldRemoveKey = useMemo(() => (value: string, key: string) => applyMoreFilterAttrs.includes(key), []);
  const { processFilters, navigateToQuery } = useProcessFilters();

  const submitFormFilters = useCallback(
    (values: FilterContextValuesType) => {
      try {
        const { query } = processFilters(values, jobListParams);
        setJobExplorerPageContent(values.page_content);
        setTimeout(() => {
          navigateToQuery(
            query,
            values?.user_location_lon ? { lon: +values.user_location_lon, lat: +values.user_location_lat } : undefined
          );
          closeModal();
        }, 300);
      } catch (error) {
        console.error(`Error on submitting data`, error);
      }
    },
    [closeModal, jobListParams, navigateToQuery, processFilters, setJobExplorerPageContent]
  );

  const resetFormFilters = useCallback(() => {
    try {
      setJobListParams((p) => {
        const listParams = omitBy({ ...p }, shouldRemoveKey);
        const newPayload = {
          ...listParams,
          lon: defaultItalyCoordinates[0],
          lat: defaultItalyCoordinates[1],
          radius: defaultJobListPayload['radius'],
          offset: 0,
        };
        return newPayload;
      });
      setJobCoordinateParams((p) => {
        const listParams = omitBy({ ...p }, shouldRemoveKey);
        const newPayload = { ...listParams };
        return newPayload;
      });
      setFilterContextValues({});
      setMoreFilters({});
      setJobExplorerPageContent('jps-and-venues');
      setTimeout(() => {
        mapRef.current?.flyTo({
          center: [defaultItalyCoordinates[0], defaultItalyCoordinates[1]],
          duration: 2000,
          // zoom: 5,
        });
        router.push(
          {
            // pathname: router.query.filter_slug ? `/cerco-lavoro/${router.query.filter_slug}` : '/cerco-lavoro',
            query: omitBy(router.query, (_, key) => key === 'overall_filters'),
          },
          null,
          {
            shallow: true,
          }
        );
        closeModal();
      }, 300);
    } catch (error) {
      console.error(error, 'Error on reset filter fields');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    closeModal,
    mapRef?.current,
    router,
    setFilterContextValues,
    setJobCoordinateParams,
    setJobListParams,
    setMoreFilters,
    shouldRemoveKey,
  ]);
  const handleFilterChange = useCallback(
    async (values?: FieldValues) => {
      if (values) submitFormFilters(values);
      else resetFormFilters();
    },
    [resetFormFilters, submitFormFilters]
  );
  useEffect(() => {
    if (isResetFilter) {
      reset({});
      setIsResetFilter(false);
    }
  }, [isResetFilter, reset, setIsResetFilter]);
  return {
    filterCount,
    handleFilterChange,
  };
};

export function useJobExplorerFiltersFromContextValues() {
  const jobListParams = useAtomValue(jobListParamsAtom);
  const { processFilters, navigateToQuery } = useProcessFilters();

  const filterValues = useCallback(
    (values: FilterContextValuesType) => {
      try {
        const { query } = processFilters(values, jobListParams);

        setTimeout(() => {
          navigateToQuery(
            query,
            values?.user_location_lon ? { lon: +values.user_location_lon, lat: +values.user_location_lat } : undefined
          );
        }, 300);
      } catch (error) {
        console.error(`Error on submitting data`, error);
      }
    },
    [jobListParams, processFilters, navigateToQuery]
  );

  return { filterValues };
}

export function useProcessFilters() {
  const router = useRouter();
  const mapRef = useAtomValue(getMapRefAtom);
  const setFilterContextValues = useSetAtom(filterContextValuesAtom);
  const setJobCoordinateParams = useSetAtom(jobCoordinateParamsAtom);
  const setJobListParams = useSetAtom(jobListParamsAtom);
  const setMoreFilters = useSetAtom(moreFiltersAtom);

  const processFilters = useCallback(
    (values: FilterContextValuesType, jobListParams: JobPositionListParamsType) => {
      setFilterContextValues({ ...values });

      const formattedFieldValues = formatExplorerOverallFilterData({ values: { ...values } });

      const mapParams = pickBy(jobListParams, (_, key: string) =>
        ['lat', 'lon', 'radius', 'offset', 'limit', 'order_by'].includes(key)
      );

      const listParams = omitBy({ ...mapParams, ...formattedFieldValues }, (v) => typeof v === 'undefined');
      const coordinateParams = omitBy({ ...formattedFieldValues }, (value) => typeof value === 'undefined');

      // coordinates api
      setJobCoordinateParams(coordinateParams);
      //list api
      setJobListParams({ ...listParams, offset: 0 });

      const filterValues = omitBy(
        formattedFieldValues,
        (value, key) =>
          typeof value === 'undefined' ||
          ['min_salary', 'max_salary', 'user_location_lat', 'user_location_lon', 'benefits'].includes(key)
      );

      setMoreFilters(filterValues);

      const query = !Object.keys(filterValues).length
        ? omitBy(router.query, (_, key) => key === 'overall_filters')
        : {
            ...router.query,
            ...(Object.keys(values).length ? { overall_filters: JSON.stringify({ ...values }) } : {}),
          };

      return { query, formattedFieldValues };
    },
    [router, setFilterContextValues, setJobCoordinateParams, setJobListParams, setMoreFilters]
  );

  const navigateToQuery = useCallback(
    (query: Record<string, any>, userLocation?: { lon: number; lat: number }) => {
      if (userLocation?.lon && mapRef?.current) {
        mapRef.current.flyTo({
          center: [userLocation.lon, userLocation.lat],
          duration: 2000,
        });
      }

      router.push(
        {
          query,
        },
        null,
        { shallow: true }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router, mapRef?.current]
  );

  return { processFilters, navigateToQuery };
}
